/**
 * Global navigation bar — rendered inside ProtectedLayout so every
 * authenticated page gets it automatically.
 *
 * Structure:
 *   Left  — FinminOS logo (links to /dashboard)
 *   Right — Dashboard link  |  User avatar + name → dropdown (Profile / Referrals / Logout)
 *
 * Colors use inline CSS-variable styles with hardcoded fallbacks so they
 * render immediately without depending on Tailwind JIT.
 */
import * as React from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  Coins,
  Gift,
  LayoutDashboard,
  Loader2,
  LogOut,
  Pencil,
  Plus,
  User,
  X,
} from 'lucide-react';
import { useAuth } from '@finmin/auth';
import { BrandLogo, Button, cn, ErrorMessage, Input, toast } from '@finmin/ui';
import { NotificationBell } from '@/features/notifications/components/notification-bell';
import {
  getPanItemState,
  isAllSelectable,
  isValidPanFormat,
  normalizePan,
  panDisplayName,
  panTypeColor,
  panTypeLabel,
  PAN_LABEL_MAX_LENGTH,
  PAN_TYPES,
  useAddPAN,
  useRenamePAN,
  type CustomerPAN,
  type PanConstraint,
} from '@/api/customer-pans';
import { usePanView } from '@/features/dashboard/pan-view-context';

// ── Palette constants (same pattern as auth-layout) ───────────────────────────
const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const AMBER = 'hsl(var(--palette-amber,   33 100% 50%))';
const CREAM = 'hsl(var(--palette-cream,   40 100% 98%))';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** PANs grouped by entity type, in the canonical type order. */
function groupPansByType(pans: CustomerPAN[]): [string, CustomerPAN[]][] {
  return PAN_TYPES.map(
    (type) => [type, pans.filter((p) => p.pan_type === type)] as [string, CustomerPAN[]],
  ).filter(([, group]) => group.length > 0);
}

/** One line explaining why some PANs are greyed out on this page. */
function constraintNotice(constraint: PanConstraint): string | null {
  if (constraint.kind === 'locked') return 'This request is filed under one PAN';
  return null;
}

function PanMenuItem({
  label,
  sublabel,
  selected,
  state,
  onSelect,
  onRename,
  renameLabel,
  color,
  mono,
}: {
  label: string;
  /** Second line. Falls back to the disabled reason, which always wins. */
  sublabel?: string;
  selected: boolean;
  state: { disabled: boolean; reason?: string };
  onSelect: () => void;
  /** When given, a pencil appears on hover/focus to rename this row. */
  onRename?: () => void;
  renameLabel?: string;
  color?: string;
  mono?: boolean;
}) {
  // "Why can't I pick this" beats "which one is this", so a disabled reason
  // takes the sub-line even when the PAN has a name (the name holds line 1).
  const secondLine = state.disabled && state.reason ? state.reason : sublabel;

  return (
    <div
      role="menuitem"
      className={cn(
        'group relative flex items-center border-l-2 transition-colors',
        state.disabled ? 'opacity-40' : 'hover:bg-muted',
        selected ? 'border-accent bg-accent/[0.06]' : 'border-transparent',
      )}
    >
      <button
        type="button"
        tabIndex={state.disabled ? -1 : 0}
        disabled={state.disabled}
        aria-disabled={state.disabled}
        title={state.reason}
        onClick={state.disabled ? undefined : onSelect}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left',
          state.disabled && 'cursor-not-allowed',
        )}
      >
        <span
          className={cn(
            'h-2 w-2 shrink-0 rounded-full',
            !color && (selected ? 'bg-accent' : 'border-muted-foreground border-[1.5px]'),
          )}
          style={color ? { backgroundColor: color } : undefined}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'text-foreground block truncate text-sm',
              mono ? 'font-mono tracking-wider' : 'font-medium',
            )}
          >
            {label}
          </span>
          {secondLine && (
            <span
              className={cn(
                'text-muted-foreground block truncate text-[11px]',
                // The PAN under a name stays mono; a disabled reason is prose.
                !(state.disabled && state.reason) && 'font-mono tracking-wider',
              )}
            >
              {secondLine}
            </span>
          )}
        </span>
      </button>

      {onRename && (
        <button
          type="button"
          onClick={onRename}
          aria-label={renameLabel}
          title={renameLabel}
          className={cn(
            'text-muted-foreground hover:text-foreground mr-1.5 shrink-0 rounded p-1.5',
            'opacity-0 transition-opacity focus:opacity-100 focus-visible:ring-1',
            'group-hover:opacity-100 group-focus-within:opacity-100',
          )}
        >
          <Pencil className="h-3 w-3" aria-hidden />
        </button>
      )}
    </div>
  );
}

/** Inline rename form — replaces a PAN row while that row is being renamed. */
function RenamePanRow({
  pan,
  onDone,
}: {
  pan: CustomerPAN;
  onDone: () => void;
}) {
  const [value, setValue] = React.useState(pan.label ?? '');
  const [error, setError] = React.useState<string | null>(null);
  const rename = useRenamePAN({
    onSuccess: (updated) => {
      toast.success(updated.label ? 'Name updated.' : 'Name removed.');
      onDone();
    },
    onError: (e) => setError(e.message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // Empty clears the name — that is the "remove name" path, no separate control.
    rename.mutate({ id: pan.id, label: value });
  }

  return (
    <form onSubmit={submit} className="space-y-1.5 px-3 py-2">
      <div className="flex items-center gap-1.5">
        <Input
          value={value}
          onChange={(e) => {
            setValue(e.target.value.slice(0, PAN_LABEL_MAX_LENGTH));
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onDone();
            }
          }}
          placeholder={pan.pan}
          maxLength={PAN_LABEL_MAX_LENGTH}
          autoFocus
          aria-label={`Name for ${pan.pan}`}
          className="h-8 text-xs"
        />
        <Button type="submit" size="sm" className="h-8 shrink-0" disabled={rename.isPending}>
          {rename.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onDone}
          aria-label="Cancel renaming"
          className="h-8 w-8 shrink-0 p-0"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
      <p className="text-muted-foreground text-[10px]">
        Leave blank to remove the name.
      </p>
      <ErrorMessage message={error} />
    </form>
  );
}

/** Inline "add a PAN" row shown at the bottom of the PAN switcher. */
function AddPanRow() {
  const [adding, setAdding] = React.useState(false);
  const [value, setValue] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const add = useAddPAN({
    onSuccess: () => {
      toast.success('PAN added.');
      setAdding(false);
      setValue('');
      setLabel('');
    },
    onError: (e) => setError(e.message),
  });

  if (!adding) {
    return (
      <button
        type="button"
        role="menuitem"
        onClick={() => setAdding(true)}
        className="text-foreground hover:bg-muted flex w-full items-center gap-2.5 px-3 py-2.5 text-sm transition-colors"
      >
        <Plus className="text-muted-foreground h-3.5 w-3.5 shrink-0" aria-hidden />
        Add PAN
      </button>
    );
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const normalized = normalizePan(value);
    if (!isValidPanFormat(normalized)) {
      setError('Enter a valid PAN (e.g. ABCDE1234F).');
      return;
    }
    setError(null);
    add.mutate({ pan: normalized, label: label.trim() || null });
  }

  return (
    <form onSubmit={submit} className="space-y-1.5 px-3 py-2">
      <div className="flex items-center gap-1.5">
        <Input
          value={value}
          onChange={(e) => {
            setValue(e.target.value.toUpperCase().slice(0, 10));
            setError(null);
          }}
          placeholder="ABCDE1234F"
          maxLength={10}
          autoCapitalize="characters"
          autoFocus
          aria-label="PAN number"
          className="h-8 font-mono text-xs uppercase tracking-wider"
        />
        <Button type="submit" size="sm" className="h-8 shrink-0" disabled={add.isPending}>
          {add.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
      </div>
      {/* Optional — a name makes the PAN easier to recognise later. */}
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value.slice(0, PAN_LABEL_MAX_LENGTH))}
        placeholder="Name (optional)"
        maxLength={PAN_LABEL_MAX_LENGTH}
        aria-label="Name for this PAN (optional)"
        className="h-8 text-xs"
      />
      <ErrorMessage message={error} />
    </form>
  );
}

/** "Rohan Sharma" → "RS" | "+919876543210" → "?" | "rohan" → "R" */
function getInitials(name: string): string {
  if (!name) return '?';
  if (name.startsWith('+')) return '?'; // phone number fallback
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]![0]!.toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// ── NavBar ────────────────────────────────────────────────────────────────────

export function NavBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const { selection, setSelection, pans, selectedPan, constraint } = usePanView();
  const [panOpen, setPanOpen] = React.useState(false);
  const panMenuRef = React.useRef<HTMLDivElement>(null);
  // Only one row may be renamed at a time.
  const [editingPanId, setEditingPanId] = React.useState<string | null>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
    }
  }, [open]);

  // Close PAN switcher when clicking outside
  React.useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (panMenuRef.current && !panMenuRef.current.contains(e.target as Node)) {
        setEditingPanId(null);
        setPanOpen(false);
      }
    }
    if (panOpen) {
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
    }
  }, [panOpen]);

  /** Logout: clear tokens → clear query cache → navigate to the public landing page */
  function handleLogout() {
    setOpen(false);
    logout();
    queryClient.clear();
    void navigate({ to: '/', replace: true });
  }

  const displayName = user?.name || user?.mobile_e164 || 'Account';
  const initials = getInitials(displayName);

  return (
    <nav
      className="sticky top-0 z-50 w-full"
      style={{ backgroundColor: FOREST, borderBottom: `1px solid rgba(255,255,255,0.08)` }}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        {/* ── FinminOS logo ─────────────────────────────────────────────────
              Mobile (< sm): just the amber coin (saves horizontal real estate
              for the dashboard link + avatar dropdown on the right).
              Tablet+ (≥ sm): full "FinminOS" wordmark. */}
        <Link
          to="/dashboard"
          aria-label="FinminOS — go to dashboard"
          className="flex shrink-0 items-center rounded-sm transition-transform duration-200 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
        >
          <span className="sm:hidden">
            <BrandLogo wordColor="cream" height={26} />
          </span>
          <span className="hidden sm:block">
            <BrandLogo wordColor="cream" height={38} />
          </span>
        </Link>

        {/* ── Right side ────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-1">
          {/* Dashboard link */}
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors"
            style={{
              color: CREAM,
              opacity: pathname === '/dashboard' ? 1 : 0.7,
              backgroundColor: pathname === '/dashboard' ? 'rgba(255,255,255,0.1)' : 'transparent',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.08)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor =
                pathname === '/dashboard' ? 'rgba(255,255,255,0.1)' : 'transparent';
            }}
          >
            <LayoutDashboard className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>

          {/* Notifications */}
          <NotificationBell />

          {/* PAN switcher */}
          <div ref={panMenuRef} className="relative">
            <button
              type="button"
              onClick={() => {
                // Never reopen into a stale edit form.
                setEditingPanId(null);
                setPanOpen((v) => !v);
              }}
              aria-haspopup="true"
              aria-expanded={panOpen}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors"
              style={{ color: CREAM, opacity: 0.85 }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.08)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              {selectedPan && (
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: panTypeColor(selectedPan.pan_type) }}
                  aria-hidden
                />
              )}
              <span
                className={cn(
                  'max-w-[12ch] truncate text-xs',
                  // Prose names are not letter-spaced; raw PANs still are.
                  selectedPan?.label?.trim() ? 'font-medium' : 'font-mono tracking-wider',
                )}
                title={selectedPan?.pan}
              >
                {selection === 'all' ? 'All PANs' : (panDisplayName(selectedPan) || 'PAN')}
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${panOpen ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>

            {panOpen && (
              <div
                className="border-border bg-background animate-slide-down-fade absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border py-1 shadow-lg"
                role="menu"
              >
                {constraintNotice(constraint) && (
                  <p className="text-muted-foreground border-border border-b px-3 py-2 text-[11px] leading-snug">
                    {constraintNotice(constraint)}
                  </p>
                )}

                {/* All PANs — unfiltered view, not tied to any one PAN */}
                <PanMenuItem
                  label="All PANs"
                  selected={selection === 'all'}
                  state={isAllSelectable(constraint)}
                  onSelect={() => {
                    setPanOpen(false);
                    setSelection('all');
                  }}
                />

                <div className="bg-border mx-1 my-1 h-px" />

                {groupPansByType(pans).map(([panType, group]) => (
                  <div key={panType}>
                    <p
                      className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider"
                      style={{ color: panTypeColor(panType) }}
                    >
                      {panTypeLabel(panType)}
                    </p>
                    {group.map((pan) => {
                      const named = Boolean(pan.label?.trim());
                      return editingPanId === pan.id ? (
                        <RenamePanRow
                          key={pan.id}
                          pan={pan}
                          onDone={() => setEditingPanId(null)}
                        />
                      ) : (
                        <PanMenuItem
                          key={pan.id}
                          label={panDisplayName(pan)}
                          // Named rows keep the PAN on the sub-line so the user
                          // can still verify what they are filing under.
                          sublabel={named ? pan.pan : undefined}
                          mono={!named}
                          color={panTypeColor(pan.pan_type)}
                          selected={selection === pan.id}
                          state={getPanItemState(pan, constraint)}
                          onSelect={() => {
                            setEditingPanId(null);
                            setPanOpen(false);
                            setSelection(pan.id);
                          }}
                          onRename={() => setEditingPanId(pan.id)}
                          renameLabel={`Rename ${pan.pan}`}
                        />
                      );
                    })}
                  </div>
                ))}

                <div className="bg-border mx-1 my-1 h-px" />

                <AddPanRow />
              </div>
            )}
          </div>

          {/* User dropdown */}
          <div ref={menuRef} className="relative ml-2">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={open}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
              style={{ color: CREAM }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.08)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              {/* Avatar */}
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                style={{ backgroundColor: AMBER, color: '#fff' }}
                aria-hidden="true"
              >
                {initials}
              </span>

              {/* Name */}
              <span
                className="hidden max-w-[140px] truncate text-sm font-medium sm:block"
                style={{ color: CREAM }}
              >
                {displayName}
              </span>

              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                style={{ color: CREAM, opacity: 0.6 }}
                aria-hidden
              />
            </button>

            {/* ── Dropdown menu ──────────────────────────────────────────────── */}
            {open && (
              <div
                className="border-border bg-background animate-slide-down-fade absolute right-0 top-full z-50 mt-2 w-52 rounded-lg border py-1 shadow-lg"
                role="menu"
              >
                {/* User info header */}
                <div className="border-border border-b px-4 py-3">
                  <p className="text-foreground truncate text-sm font-medium">{displayName}</p>
                  {user?.mobile_e164 && (
                    <p className="text-muted-foreground mt-0.5 text-xs">{user.mobile_e164}</p>
                  )}
                </div>

                {/* Profile link */}
                <Link
                  to="/profile"
                  search={{ tab: 'profile' }}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="text-foreground hover:bg-muted flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors"
                >
                  <User className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
                  My Profile
                </Link>

                {/* Rewards link */}
                <Link
                  to="/rewards"
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="text-foreground hover:bg-muted flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors"
                >
                  <Coins className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
                  Rewards &amp; Wallet
                </Link>

                {/* Referrals link */}
                <Link
                  to="/referrals"
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="text-foreground hover:bg-muted flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors"
                >
                  <Gift className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
                  My Referrals
                </Link>

                <div className="bg-border mx-1 my-1 h-px" />

                {/* Logout */}
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="text-destructive hover:bg-destructive/5 flex w-full items-center gap-2.5 px-4 py-2.5 text-sm transition-colors"
                >
                  <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
